import { Select } from 'antd'
import { useDate } from 'app/lib/hooks/toolBox'
import { useQuery } from 'app/lib/hooks/useQuery'
import useTranslation from 'app/lib/hooks/useTranslation'
import { INCIDENT_SUBJECTS_QUERY } from 'app/queries/Incidents'
import { debounce } from 'lodash'
import { useMemo, useState } from 'react'

import { IncidentSubject, IncidentSubjectType } from './typing'

const TYPES: IncidentSubjectType[] = ['LineItem', 'Rental', 'Project']

// Record pages pass dates their normalizers already formatted for display.
const ISO_DATE = /^\d{4}-\d{2}-\d{2}/

export const subjectValue = ({ type, id }: Pick<IncidentSubject, 'type' | 'id'>) => `${type}:${id}`

type Props = {
  value?: string
  onChange?: (value: string) => void
  initialSubject?: IncidentSubject
  id?: string
}

const SubjectSelect: React.FC<Props> = ({ value, onChange, initialSubject, id }) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState<string>()
  const { data, loading } = useQuery<{ incidentSubjects: IncidentSubject[] }>(
    INCIDENT_SUBJECTS_QUERY,
    { variables: { query: search }, fetchPolicy: 'cache-and-network' }
  )
  const onSearch = useMemo(() => debounce(setSearch, 400), [])

  const subjects = data?.incidentSubjects || []
  const withInitial =
    initialSubject &&
    !subjects.some((subject) => subjectValue(subject) === subjectValue(initialSubject))
      ? [initialSubject, ...subjects]
      : subjects

  const { dayjsify } = useDate()

  const title = (subject: IncidentSubject) =>
    subject.type === 'LineItem' && subject.rentalCode
      ? `${subject.label} (${subject.rentalCode})`
      : subject.label

  const details = (subject: IncidentSubject) =>
    [
      subject.startDate &&
        [subject.startDate, subject.endDate]
          .filter(Boolean)
          .map((date) => (ISO_DATE.test(date) ? dayjsify.convertString(date.slice(0, 10)) : date))
          .join(' – '),
      subject.projectName
    ]
      .filter(Boolean)
      .join(' · ')

  const options = TYPES.map((type) => ({
    label: t({ id: `record.incident.subject_types.${type}` }),
    options: withInitial
      .filter((subject) => subject.type === type)
      .map((subject) => ({
        value: subjectValue(subject),
        title: title(subject),
        label: (
          <div>
            <div>{title(subject)}</div>
            <If condition={details(subject)}>
              <small style={{ opacity: 0.75 }}>{details(subject)}</small>
            </If>
          </div>
        )
      }))
  })).filter((group) => group.options.length)

  return (
    <Select
      id={id}
      showSearch
      allowClear
      value={value}
      onChange={onChange}
      onSearch={onSearch}
      filterOption={false}
      loading={loading}
      options={options}
      optionLabelProp="title"
      placeholder={t({ id: 'record.incident.attributes.subject_placeholder' })}
      notFoundContent={t({ id: 'record.incident.attributes.subject_empty' })}
    />
  )
}

export default SubjectSelect
